import React from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { isAuthError } from '../../api/client';
import { canAccessAdmin } from '../../domain/auth/role';
import { isoToDatePart, isoToTimeHHMM } from '../../domain/events/iso';
import { guestAccessErrorMessage } from '../../domain/guestAccess/errors';
import {
  GUEST_ACCESS_GROUPS,
  isBlockedByVipExclusion,
  toLocalDateTime,
} from '../../domain/guestAccess/guestAccess';
import { getResourceLabel } from '../../resources/resources';
import { guestAccessService } from '../../services/singletons';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { uiActions } from '../../store/slices/uiSlice';
import { TavernField } from '../../ui/components';
import { formatDateYYYYMMDD } from '../../utils/date';
import '../EventForm/EventFormPage.css';

import './GuestAccessPage.css';

export function GuestAccessFormPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { ruleId } = useParams<{ ruleId: string }>();
  const isEdit = ruleId != null;
  const role = useAppSelector((s) => s.auth.user?.role);
  const canAccess = canAccessAdmin(role);

  const today = React.useMemo(() => formatDateYYYYMMDD(new Date()), []);
  const [date, setDate] = React.useState(today);
  const [startTime, setStartTime] = React.useState('18:00');
  const [endTime, setEndTime] = React.useState('22:00');
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [notifyTelegram, setNotifyTelegram] = React.useState(true);
  const [busy, setBusy] = React.useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [prefilled, setPrefilled] = React.useState(!isEdit);

  React.useEffect(() => {
    if (!isEdit || prefilled || !canAccess) return;
    let cancelled = false;
    void (async () => {
      try {
        const rule = (await guestAccessService.listRules()).find((r) => String(r.id) === ruleId);
        if (cancelled) return;
        if (!rule) {
          dispatch(uiActions.setNotification({ type: 'error', message: 'Правило не найдено' }));
          navigate('/guests', { replace: true });
          return;
        }
        setDate(isoToDatePart(rule.startAt) ?? today);
        setStartTime(isoToTimeHHMM(rule.startAt) ?? '18:00');
        setEndTime(isoToTimeHHMM(rule.endAt) ?? '22:00');
        setSelected(new Set(rule.resourceIds));
        setNotifyTelegram(rule.notifyTelegram);
        setPrefilled(true);
      } catch (error) {
        if (!isAuthError(error)) {
          dispatch(
            uiActions.setNotification({
              type: 'error',
              message: guestAccessErrorMessage(error, 'Не удалось загрузить правило'),
            }),
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canAccess, dispatch, isEdit, navigate, prefilled, ruleId, today]);

  const timeValid = Boolean(date) && Boolean(startTime) && Boolean(endTime) && startTime < endTime;
  const inPast = timeValid && new Date(`${date}T${endTime}:00`).getTime() <= Date.now();

  // Tables that already host an organizer/admin event in the chosen window cannot be opened.
  React.useEffect(() => {
    if (!canAccess || !timeValid || inPast) {
      setBusy(new Set());
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const ids = await guestAccessService.listBusyResources(
          toLocalDateTime(date, startTime),
          toLocalDateTime(date, endTime),
        );
        if (cancelled) return;
        const next = new Set(ids);
        setBusy(next);
        setSelected((prev) => (ids.some((id) => prev.has(id)) ? new Set([...prev].filter((id) => !next.has(id))) : prev));
      } catch {
        if (!cancelled) setBusy(new Set());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canAccess, date, endTime, inPast, startTime, timeValid]);

  const toggle = React.useCallback((resourceId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(resourceId)) next.delete(resourceId);
      else next.add(resourceId);
      return next;
    });
  }, []);

  const timeError = !timeValid
    ? 'Время окончания должно быть позже времени начала'
    : inPast
      ? 'Время окончания уже прошло'
      : null;
  const isValid = timeError == null && selected.size > 0;

  const onSubmit = React.useCallback(async () => {
    if (!isValid || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const command = {
        startAt: toLocalDateTime(date, startTime),
        endAt: toLocalDateTime(date, endTime),
        resourceIds: [...selected],
        notifyTelegram,
      };
      if (isEdit && ruleId) await guestAccessService.updateRule(Number(ruleId), command);
      else await guestAccessService.createRule(command);
      dispatch(
        uiActions.setNotification({
          type: 'success',
          message: isEdit ? 'Гостевой доступ обновлён' : 'Гостевой доступ создан',
        }),
      );
      navigate('/guests', { replace: true });
    } catch (error) {
      if (!isAuthError(error)) {
        dispatch(
          uiActions.setNotification({
            type: 'error',
            message: guestAccessErrorMessage(error, 'Не удалось сохранить гостевой доступ'),
          }),
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [date, dispatch, endTime, isEdit, isSubmitting, isValid, navigate, notifyTelegram, ruleId, selected, startTime]);

  if (!canAccess) {
    return <Navigate to="/profile" replace />;
  }

  return (
    <div className="ttbPage ttbEventFormPage">
      <div className="ttbFormContainer">
        <div className="ttbFormHeader">
          <button
            type="button"
            onClick={() => navigate(-1)}
            disabled={isSubmitting}
            className="ttbButton ttbButtonOutlineHeader"
          >
            ← Назад
          </button>
          <div className="ttbFormHeaderTitle">{isEdit ? 'Редактировать доступ' : 'Создать гостевой доступ'}</div>
          <button
            type="button"
            className="ttbButton ttbButtonOutlineHeader ttbFormHeaderSpacer"
            disabled
            aria-hidden="true"
            tabIndex={-1}
          >
            ← Назад
          </button>
        </div>

        <div className="ttbFormSection">
          <div className="ttbFormSectionHeader">
            <span className="ttbFormSectionIcon">📅</span>
            <h2 className="ttbFormSectionTitle">Время</h2>
          </div>

          <TavernField label="Дата" required>
            <input
              type="date"
              value={date}
              min={today}
              disabled={isSubmitting}
              onChange={(e) => e.target.value && setDate(e.target.value)}
              className="ttbInput"
            />
          </TavernField>

          <TavernField label="Время начала" required>
            <input
              type="time"
              step={900}
              value={startTime}
              disabled={isSubmitting}
              onChange={(e) => setStartTime(e.target.value)}
              className="ttbInput"
            />
          </TavernField>

          <TavernField label="Время окончания" required error={timeError} hint="Шаг 15 минут">
            <input
              type="time"
              step={900}
              value={endTime}
              disabled={isSubmitting}
              onChange={(e) => setEndTime(e.target.value)}
              className={['ttbInput', timeError ? 'ttbInputError' : null].filter(Boolean).join(' ')}
            />
          </TavernField>
        </div>

        <div className="ttbFormSection">
          <div className="ttbFormSectionHeader">
            <span className="ttbFormSectionIcon">🎲</span>
            <h2 className="ttbFormSectionTitle">Столы</h2>
          </div>

          {GUEST_ACCESS_GROUPS.map((group) => (
            <div key={group.title} className="guest-access-form-group">
              <div className="guest-access-form-group__title">{group.title}</div>
              {group.resourceIds.map((resourceId) => {
                const isBusy = busy.has(resourceId);
                const vipBlocked = isBlockedByVipExclusion(resourceId, selected);
                const disabled = isSubmitting || isBusy || (vipBlocked && !selected.has(resourceId));
                return (
                  <label
                    key={resourceId}
                    className={`guest-access-form-check${disabled ? ' guest-access-form-check--disabled' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(resourceId)}
                      disabled={disabled}
                      onChange={() => toggle(resourceId)}
                    />
                    <span>
                      {getResourceLabel(resourceId)}
                      {isBusy ? ' — занят мероприятием' : vipBlocked ? ' — пересекается с VIP-столами' : ''}
                    </span>
                  </label>
                );
              })}
            </div>
          ))}
          <div className="ttbFieldHint">Стол VIP занимает столы 5 и 6 целиком, поэтому их нельзя открыть вместе.</div>
        </div>

        <div className="ttbFormSection">
          <label className="guest-access-form-check">
            <input
              type="checkbox"
              checked={notifyTelegram}
              disabled={isSubmitting || isEdit}
              onChange={(e) => setNotifyTelegram(e.target.checked)}
            />
            <span>🔔 Уведомить в Telegram об открытии доступа{isEdit ? ' (отправляется при создании)' : ''}</span>
          </label>
        </div>

        <div className="ttbFormActions">
          <button
            type="button"
            onClick={() => navigate(-1)}
            disabled={isSubmitting}
            className="ttbButton ttbButtonOutlineHeader"
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={() => void onSubmit()}
            disabled={!isValid || isSubmitting || !prefilled}
            className="ttbButton ttbButtonSubmit"
          >
            {isSubmitting ? 'Сохранение…' : isEdit ? '✨ Сохранить' : '✨ Создать'}
          </button>
        </div>
      </div>
    </div>
  );
}
